<template>
  <div class="bg-canvas px-9 py-8 rounded-lg w-[310px] flex flex-col items-center gap-3 drop-shadow-8">
    <img :src="getFullPathForBrandedAssets(data.imageUrl)" :alt="data.title" />
    <div class="font-medium heading3">{{ data.title }}</div>
    <div class="w-8 h-2" :class="tinted.bg" />
    <div class="mt-4 self-stretch flex flex-col -mx-4">
      <component
        :is="entry.href.includes('/search') ? 'a' : LinkWrapper"
        v-for="[index, entry] in computedLinks.entries()"
        :key="index"
        :href="entry.href"
        class="flex gap-3 px-4 py-3 rounded-lg cursor-pointer"
        :class="tinted.hover"
      >
        <v-icon
          :size="-5"
          :name="entry.icon"
          :class="tinted.icon"
          class="h-5 w-[22px] shrink-0"
          :aria-label="entry.icon"
        />
        <div class="base-bold" :class="tinted.text">{{ entry.text }}</div>
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { VIcon } from '@vonage/vivid-vue';
import { NavCard } from '@bifrost-dev-docs-mfe/common/types/compositeComponents/NavCards';
import { getFullPathForBrandedAssets, showCommunityLink, showPricingLink } from '@/utils/url';
import LinkWrapper from '@/components/markdown/widgets/LinkWrapper.vue';

const props = defineProps({
  data: {
    type: Object as PropType<NavCard>,
    required: true,
  },
});

const tinted = computed(() => {
  switch (props.data.tint) {
    case 'purple':
      return {
        bg: 'bg-purple-500',
        icon: 'var(--dp-color-cta-500)',
        text: 'text-purple-900',
        hover: 'hover:bg-purple-500',
      };
    case 'blue':
      return {
        bg: 'bg-blue-400',
        icon: 'text-blue-400',
        text: 'text-blue-900',
        hover: 'hover:bg-blue-50',
      };
    case 'orange':
      return {
        bg: 'bg-orange-300',
        icon: 'text-orange-300',
        text: 'text-orange-900',
        hover: 'hover:bg-orange-50',
      };
    default:
      throw new Error(`Unknown tint: ${props.data.tint}`);
  }
});
const computedLinks = props.data.links.filter(key => showCommunityLink(key.href) && showPricingLink(key.href));
</script>

<style lang="scss" scoped>
// Remove default styles
a {
  margin: 0 !important;
  text-decoration: none !important;
}
.base-bold {
  font: var(--vvd-typography-base-bold);
}
.bg-blue-400 {
  background: var(--dev-docs-discover-section-bg);
}
.text-blue-400 {
  color: var(--dev-docs-discover-section-bg);
}
.hover\:bg-blue-50:hover {
  background-color: var(--dev-docs-build-section-hover-color);
}
.hover\:bg-purple-500:hover {
  background-color: var(--dev-docs-discover-hover-color);
}
.bg-purple-500 {
  background: var(--dev-docs-build-section-bg);
}
.bg-orange-300 {
  background: var(--dev-docs-manage-section-bg);
}
.text-orange-300 {
  color: var(--dev-docs-manage-section-bg);
}
.hover\:bg-orange-50:hover {
  background-color: var(--dev-docs-manage-section-hover-color);
}
</style>
